import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ChevronDown, Menu, Search, X } from "lucide-react";
import StockSearch from "./StockSearch";

// Market data pages (backend plan 300): one entry per country; each country page links to all of
// its data pages (market today, stocks, events, Shariah ratios, funds). Add a country here.
const MARKETS = [
  { to: "/markets/bd", label: "Bangladesh", detail: "DSE stocks, technical reports, events, Shariah ratios, mutual funds" },
];

const PRODUCT = [
  { to: "/backtesting", label: "Backtesting", detail: "Test strategies on DSE and global prices" },
  { to: "/ai-apps", label: "AI apps (MCP)", detail: "Use AnalystBro data in Claude, ChatGPT, Gemini" },
  { href: "/#download", label: "Download", detail: "The AnalystBro desktop app" },
];

// A hover/click dropdown. `items`: {to | href, label, detail}; `footer`: optional {to, label}.
function NavMenu({ item, label, heading, items, footer }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const row = "block px-3 py-2 rounded-lg hover:bg-[var(--ab-accent-soft)]";
  const body = (m) => (
    <>
      <span className="block text-sm font-medium text-[var(--ab-text)]">{m.label}</span>
      <span className="block text-xs text-[var(--ab-muted)]">{m.detail}</span>
    </>
  );
  return (
    <div className="relative" ref={ref} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" className={`${item} inline-flex items-center gap-1`} aria-haspopup="true"
              aria-expanded={open} onClick={() => setOpen(true)}>
        {label} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-1 z-50">
          <div className="min-w-[16rem] rounded-xl border border-[var(--ab-border)] bg-[var(--ab-surface)] shadow-lg p-2">
            {heading && (
              <div className="px-3 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--ab-muted)]">
                {heading}
              </div>
            )}
            {items.map((m) =>
              m.to ? (
                <Link key={m.label} to={m.to} onClick={() => setOpen(false)} className={row}>{body(m)}</Link>
              ) : (
                <a key={m.label} href={m.href} onClick={() => setOpen(false)} className={row}>{body(m)}</a>
              ))}
            {footer && (
              <Link to={footer.to} onClick={() => setOpen(false)}
                    className="block mt-1 px-3 py-1.5 border-t border-[var(--ab-border)] text-xs font-medium text-[var(--ab-accent)]">
                {footer.label}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// One link per destination. `theme` is "ab-light" or "ab-dark" (see index.css);
// pages that are not yet on the new look keep the dark set.
export default function AnalystBroNavbar({ theme = "ab-dark" }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const item =
    "px-3 py-2 rounded-lg text-sm font-medium text-[var(--ab-muted)] hover:text-[var(--ab-text)] transition-colors";
  const mobileItem =
    "block px-3 py-3 rounded-lg text-base font-medium text-[var(--ab-text)] hover:bg-[var(--ab-accent-soft)]";
  const primary =
    "inline-flex items-center justify-center gap-1.5 rounded-lg bg-[var(--ab-accent)] px-4 py-2 text-sm font-semibold text-[var(--ab-on-accent)] hover:bg-[var(--ab-accent-hover)] transition";

  return (
    <header
      className={`${theme} sticky top-0 z-50 w-full border-b border-[var(--ab-border)] backdrop-blur-md`}
      style={{ background: "var(--ab-header)" }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-6 shrink-0">
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/analystbro-logo.png" alt="" className="ab-logo h-7 w-7" />
            <span className="font-display font-bold text-lg tracking-tight">AnalystBro</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavMenu item={item} label="Markets" heading="Countries" items={MARKETS}
                     footer={{ to: "/markets", label: "All markets" }} />
            <NavMenu item={item} label="Product" items={PRODUCT} />
            <a href="/research" className={item}>Research</a>
            <a href="/#enterprise" className={item}>Enterprise</a>
          </nav>
        </div>

        <StockSearch className="hidden lg:block flex-1 max-w-sm min-w-[14rem]" />

        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={() => setSearchOpen((v) => !v)}
            className="lg:hidden p-2 text-[var(--ab-muted)] hover:text-[var(--ab-text)] rounded-lg"
            aria-label="Search symbols"
          >
            <Search size={18} />
          </button>
          {isAuthenticated ? (
            <Link to="/agentbro" className={primary}>Open AnalystBro</Link>
          ) : (
            <>
              <Link to="/login" className={item}>Log in</Link>
              <Link to="/agentbro" className={primary}>Launch Agent</Link>
            </>
          )}
        </div>

        {/* Phone: log in is the main job, so it sits next to the menu button. */}
        <div className="flex md:hidden items-center gap-1">
          {/* The homepage has its own big log-in button on phones. */}
          {location.pathname !== "/" && (
            <Link to={isAuthenticated ? "/agentbro" : "/login"} className={primary}>
              {isAuthenticated ? "Open" : "Log in"}
            </Link>
          )}
          <button
            onClick={() => setSearchOpen((v) => !v)}
            className="p-2.5 text-[var(--ab-muted)] hover:text-[var(--ab-text)] rounded-lg"
            aria-label="Search symbols"
            aria-expanded={searchOpen}
          >
            <Search size={20} />
          </button>
          <button
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="p-2.5 text-[var(--ab-muted)] hover:text-[var(--ab-text)] rounded-lg"
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="lg:hidden border-t border-[var(--ab-border)] bg-[var(--ab-surface)] px-4 py-3">
          <StockSearch autoFocus onDone={() => setSearchOpen(false)} />
        </div>
      )}

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[var(--ab-border)] bg-[var(--ab-surface)] px-4 py-3">
          <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-[var(--ab-muted)]">Markets</div>
          {MARKETS.map((m) => (
            <Link key={m.to} to={m.to} className={mobileItem}>{m.label}</Link>
          ))}
          <Link to="/markets" className={mobileItem}>All markets</Link>
          <div className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-[var(--ab-muted)]">Product</div>
          {PRODUCT.map((m) =>
            m.to ? <Link key={m.label} to={m.to} className={mobileItem}>{m.label}</Link>
              : <a key={m.label} href={m.href} onClick={() => setMobileMenuOpen(false)} className={mobileItem}>{m.label}</a>)}
          <div className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-[var(--ab-muted)]">Company</div>
          <a href="/research" className={mobileItem}>Research</a>
          <a href="/#enterprise" onClick={() => setMobileMenuOpen(false)} className={mobileItem}>Enterprise</a>
          <a href="/#contact" onClick={() => setMobileMenuOpen(false)} className={mobileItem}>Contact</a>
        </div>
      )}
    </header>
  );
}
